import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentService } from '../../../services/document.service';
import type { DrawingEvidence } from '../../../types/drbfm';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';
import { selectPageImage } from './evidenceImage';

interface DrawingEvidenceImageState {
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
}

export const useDrawingEvidenceImage = (
  evidence: DrawingEvidence | undefined,
  open: boolean,
): DrawingEvidenceImageState => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !evidence) {
      setImageUrl(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrlToRevoke: string | null = null;

    const loadEvidenceImage = async () => {
      if (!evidence.documentId) {
        setError(t('drbfm.drawingEvidence.missingDocumentId', '図面根拠の文書IDがありません。'));
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setImageUrl(null);

        let imageId = evidence.imageId;
        if (!imageId) {
          const imageData = await documentService.getDocumentImages(evidence.documentId);
          const selectedImage = selectPageImage(imageData.images || [], evidence.pageNumber);
          imageId = selectedImage?.id;
        }

        // Reported directly rather than thrown: this copy is already localized
        // and names the actual problem, so the catch below must not flatten it
        // to the generic load-failure fallback.
        if (!imageId) {
          if (!cancelled) {
            setError(t('drbfm.drawingEvidence.imageNotFound', '対象ページの画像が見つかりません。'));
            setLoading(false);
          }
          return;
        }

        const blobUrl = await documentService.getDocumentImageBlob(evidence.documentId, imageId);
        objectUrlToRevoke = blobUrl;
        if (!cancelled) {
          setImageUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(resolveApiErrorMessage(t, err, {
            fallbackKey: 'drbfm.drawingEvidence.imageLoadFailed',
          }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEvidenceImage();

    return () => {
      cancelled = true;
      if (
        objectUrlToRevoke
        && objectUrlToRevoke.startsWith('blob:')
        && typeof URL !== 'undefined'
        && typeof URL.revokeObjectURL === 'function'
      ) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error messages; excluded so a language change doesn't refetch the drawing image (PR #790)
  }, [evidence, open]);

  return { imageUrl, loading, error };
};
